// ANGEL FLIGHT — the Armory upgrade catalog + pure stat resolver.
//
// Two branches (GUN / BIRD). `resolvePlayerStats` folds owned tiers + difficulty
// into the live PlayerStats systems read. Pure — no Phaser.

import type {
  DifficultyDef,
  PlayerStats,
  SaveStateV1,
  UpgradeDef,
  UpgradeId,
  UpgradeTier,
} from '../core/types';
import { BALANCE } from './balance';

export const UPGRADES: Readonly<Record<UpgradeId, UpgradeDef>> = Object.freeze({
  damage: {
    id: 'damage',
    branch: 'gun',
    name: 'Hardened Rounds',
    blurb: 'Heavier slugs. More damage per round.',
    costs: [120, 280, 560, 1000],
    values: [1.15, 1.32, 1.55, 1.85],
  },
  fireRate: {
    id: 'fireRate',
    branch: 'gun',
    name: 'Spin Governor',
    blurb: 'Uncage the barrels. Faster cyclic rate.',
    costs: [140, 320, 640],
    values: [1.12, 1.26, 1.45],
  },
  ammoCap: {
    id: 'ammoCap',
    branch: 'gun',
    name: 'Ammo Feed',
    blurb: 'A longer belt before the vent.',
    costs: [100, 240, 480],
    values: [50, 110, 200],
  },
  reload: {
    id: 'reload',
    branch: 'gun',
    name: 'Quick Vent',
    blurb: 'Bleed the heat faster. Shorter reloads.',
    costs: [120, 280],
    values: [0.82, 0.66],
  },
  hull: {
    id: 'hull',
    branch: 'bird',
    name: 'Armor Plating',
    blurb: 'Bolt on plate. Soak more before you go down.',
    costs: [150, 350, 700, 1200, 2000],
    values: [130, 165, 200, 230, 260],
  },
  autoRepair: {
    id: 'autoRepair',
    branch: 'bird',
    name: 'Field Repair',
    blurb: 'Patch the hull between contacts. HP regen in the lulls.',
    costs: [260, 520, 900],
    values: [2, 4, 6],
  },
} as const);

// The Armory storefront order. The BIRD branch now reads as a real choice —
// HULL (soak a burst) vs FIELD REPAIR (sustain across the mission) — instead of
// hull being the only purchasable bird stat (autoRepair was defined + saved +
// even formatted in the Hangar, but was missing from this list, so it never
// rendered and resolvePlayerStats ignored it: a dead progression branch).
export const UPGRADE_LIST: readonly UpgradeDef[] = [
  UPGRADES.damage,
  UPGRADES.fireRate,
  UPGRADES.ammoCap,
  UPGRADES.reload,
  UPGRADES.hull,
  UPGRADES.autoRepair,
] as const;

export function maxTier(id: UpgradeId): number {
  return UPGRADES[id].costs.length;
}

export function nextTierCost(id: UpgradeId, owned: UpgradeTier): number | null {
  const def = UPGRADES[id];
  if (owned >= def.costs.length) return null;
  return def.costs[owned] ?? null;
}

/** SALVAGE price for the same tier — the field-workshop retrofit. Salvage
 *  stands in for SCRIP at BALANCE.armory.scripPerSalvage, floored at
 *  minSalvageCost so low tiers are never free. Null when maxed (same gate as
 *  nextTierCost). This is what makes salvage SPENDABLE: every upgrade tick can
 *  be bought with either wallet. */
export function salvageTierCost(id: UpgradeId, owned: UpgradeTier): number | null {
  const scrip = nextTierCost(id, owned);
  if (scrip === null) return null;
  return Math.max(
    BALANCE.armory.minSalvageCost,
    Math.ceil(scrip / BALANCE.armory.scripPerSalvage),
  );
}

function valueAt(id: UpgradeId, tier: UpgradeTier, neutral: number): number {
  if (tier <= 0) return neutral;
  const v = UPGRADES[id].values[tier - 1];
  return v ?? neutral;
}

export function resolvePlayerStats(
  save: SaveStateV1,
  diff: DifficultyDef,
): PlayerStats {
  const up = save.upgrades;
  const hull = valueAt('hull', up.hull, BALANCE.player.baseHull);
  return {
    maxHull: Math.round(hull * diff.hullMult),
    damageMult: valueAt('damage', up.damage, 1),
    fireRateMult: valueAt('fireRate', up.fireRate, 1),
    ammoCap: valueAt('ammoCap', up.ammoCap, 0),
    reloadMult: valueAt('reload', up.reload, 1),
    autoRepair: valueAt('autoRepair', up.autoRepair, 0),
  };
}
